import Pagination from '@navi/web-ui/lib/components/Pagination/Pagination';
import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { QueryParams } from 'src/components/interfaces';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import TableLoader from 'src/components/TableLoader/TableLoader';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { createQueryParams, readQueryParams } from 'src/utils/QueryParamsHelper';
import {
  fetchFieldSystemInteractions,
  fetchSystemInteractions
} from '../../actions/interactionsActions';
import {
  COMMUNICATION_HISTORY_TAB_KEYS,
  getLastMonthDate,
  getTodayDate,
  NO_RECORD_FOUND,
  SYSTEM_INTERACTIONS_FIELD_VISIT_TYPE_COL_DEFS,
  SYSTEM_INTERACTIONS_TYPE,
  SYSTEM_INTERACTIONS_TYPE_COL_DEFS,
  TCOMMUNICATION_HISTORY_TAB_VALUES
} from '../../constants/communicationHistory.constant';
import styles from './index.module.scss';
import { DropDownPosition } from '@navi/web-ui/lib/components/Pagination/constant';
import FeedbackImageLocation from '../FeedbackImageLocation/FeedbackImageLocation';
import { DateFormat, incrementDateByMins, utcToIST } from '../../../../utils/DateHelper';
import { defaultPageSize } from '../../../Cases/constants/CasesConstants';
import { TAB_KEYS } from '../../constants';
import {
  FROM_TIME,
  PREVIOUS_LOAN_ACCOUNT_INTERACTIONS,
  TO_TIME
} from '../PreviousLoanCommunicationHistory/constants';
import { InteractionType } from '../../feedbackForm/interfaces/index.type';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import cx from 'classnames';
import DownloadableLetter from './DownloadableLetter';
import DateTimePickerComponent from '@cp/src/components/DateTimePicker/DateTimePickerComponent';
import { DATE_TIME_TYPE } from '@cp/src/components/DateTimePicker/constants';
import { CustomerDetailApiResponse } from '../../interfaces/CaseDetail.type';
import Switch from '@cp/src/components/Switch/Switch';
import dayjs from 'dayjs';
import { defaultPreviousLoanInteractionsFromDateString } from '@cp/pages/CaseDetails/components/CommunicationHistory/constants';

const AgTable = React.lazy(
  () => import(/* webpackChunkName: ['table'] */ '@navi/web-ui/lib/components/AgTable/AgTable')
);

interface ISystemInteractionsProps {
  type: (typeof SYSTEM_INTERACTIONS_TYPE)[keyof typeof SYSTEM_INTERACTIONS_TYPE];
}
const SystemInteractions = (props: ISystemInteractionsProps) => {
  const { type } = props;
  const isTypeFieldVisit = type === SYSTEM_INTERACTIONS_TYPE.FIELD_VISIT;
  const {
    loanId = '',
    customerId = '',
    selectedTab = '',
    caseReferenceId = '',
    caseBusinessVertical = '',
    previousCaseReferenceId = ''
  } = useParams();
  const isPreviousLoanInteraction = selectedTab === TAB_KEYS.PREV_LOAN_INTERACTIONS;
  const maxInputDate = incrementDateByMins(utcToIST(getTodayDate()), 1).toISOString().slice(0, 16);
  const defaultLastMonthDate = utcToIST(getLastMonthDate()).toISOString().slice(0, 16);
  const defaultPreviousLoanInteractionsFromDate = dayjs(
    defaultPreviousLoanInteractionsFromDateString,
    DateFormat.ISO_DATE_FORMAT
  ).format(DateFormat.COMMUNICATION_HISTORY_DATE_FORMAT);

  const systemInteractionsTypeParams: TCOMMUNICATION_HISTORY_TAB_VALUES =
    COMMUNICATION_HISTORY_TAB_KEYS[type];
  const pageData = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]
  );

  const { data: systemInteractionsTypeData, loading: systemInteractionsTypeLoading } =
    pageData?.[systemInteractionsTypeParams] || {};

  const [searchParams] = useSearchParams();
  const {
    [systemInteractionsTypeParams]: queryParams,
    [PREVIOUS_LOAN_ACCOUNT_INTERACTIONS]: previousLoanAccountParams,
    ...rest
  } = readQueryParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const defaultPayload = () => {
    return {
      page_no: queryParams?.page || 0,
      page_size: queryParams?.size || defaultPageSize,
      customer_reachable: queryParams?.contactable,
      caseBusinessVertical: caseBusinessVertical
    };
  };

  const handleTimeChangeClear = (val: React.ChangeEvent<HTMLInputElement>, filterKey: string) => {
    val.target.value = disbursmentDate;
    updateQueryParamsAndNavigate({
      [filterKey]: val.target.value
    });
  };

  const handleOnFromDateChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    if (val.target.value === '') {
      handleTimeChangeClear(val, FROM_TIME);
    } else {
      handleTimeChange(val, FROM_TIME);
    }
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_FromDateIcon, {
      tab: 'Call'
    });
  };
  const handleOnToDateChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    handleTimeChange(val, TO_TIME);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_ToDateIcon, {
      tab: 'Call'
    });
  };

  const createCommonPayload = (loanAccount: string, caseReferenceId: string) => {
    return {
      ...defaultPayload(),
      loan_account_number: loanAccount,
      caseReferenceId: caseReferenceId,
      from: defaultPreviousLoanInteractionsFromDate,
      types: `${InteractionType.INHOUSE_FIELD_VISIT},${InteractionType.FIELD_VISIT}`
    };
  };

  const createPayloadForSystemInteraction = () => {
    const PrevCaseRefIds = pageData?.details?.data?.closedLoanAccountViews;
    if (isTypeFieldVisit && isPreviousLoanInteraction) {
      return createCommonPayload(
        previousLoanAccountParams?.previousLoanAccount,
        previousLoanAccountParams?.previousCaseReferenceId
      );
    } else if (isTypeFieldVisit) {
      return {
        ...createCommonPayload(loanId, caseReferenceId),
        from: queryParams?.[FROM_TIME] || defaultLastMonthDate,
        to: queryParams?.[TO_TIME] || maxInputDate
      };
    } else if (isPreviousLoanInteraction) {
      return {
        ...defaultPayload(),
        status: 'CLOSED',
        caseReferenceId:
          PrevCaseRefIds?.find(
            item => item.accountNumber == previousLoanAccountParams?.previousLoanAccount
          )?.caseReferenceId || '',
        communication_type: type,
        customer_reference_id: customerId
      };
    }
    return {
      ...defaultPayload(),
      status: 'ACTIVE',
      caseReferenceId: pageData?.details?.data?.caseReferenceId,
      communication_type: type,
      customer_reference_id: customerId
    };
  };

  const getSystemInteractionsData = () => {
    const payload = createPayloadForSystemInteraction();
    if (payload?.types) {
      dispatch(
        fetchFieldSystemInteractions(loanId, customerId, payload, isPreviousLoanInteraction)
      );
    } else {
      dispatch(
        fetchSystemInteractions(
          loanId,
          customerId,
          payload,
          isPreviousLoanInteraction ? previousLoanAccountParams?.previousLoanAccount : ''
        )
      );
    }
  };

  useEffect(() => {
    switch (type) {
      case SYSTEM_INTERACTIONS_TYPE.FIELD_VISIT:
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_FieldFeedbackPageLand);
        break;
      case SYSTEM_INTERACTIONS_TYPE.SMS:
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_SMSPageLand);
        break;
      case SYSTEM_INTERACTIONS_TYPE.WHATSAPP:
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_WAPageLand);
        break;
      case SYSTEM_INTERACTIONS_TYPE.IVR:
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_IVRPageLand);
        break;
      case SYSTEM_INTERACTIONS_TYPE.EMAIL:
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_EmailPageLand);
        break;
      case SYSTEM_INTERACTIONS_TYPE.PUSH_NOTIFICATION:
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_PNPageLand);
        break;
      default:
        break;
    }
  }, [type]);

  useEffect(() => {
    getSystemInteractionsData();
  }, [searchParams, type]);

  const updateQueryParamsAndNavigate = (params?: QueryParams, lastUsed?: string) => {
    const url = createQueryParams({
      [systemInteractionsTypeParams]: { ...queryParams, ...params, lastUsed },
      [PREVIOUS_LOAN_ACCOUNT_INTERACTIONS]: previousLoanAccountParams,
      ...rest
    });
    navigate(url, {
      replace: true
    });
  };

  const getCurrentPageNumber = (): number => {
    return Number(queryParams?.page) + 1 || 1;
  };

  const getCurrentPageSize = (): number => {
    return Number(queryParams?.size) || defaultPageSize;
  };

  const handlePageSizeChange = (size: number) =>
    updateQueryParamsAndNavigate({ size: size.toString(), page: 0 });

  const handlePageChange = (page: number) => {
    updateQueryParamsAndNavigate({ page: (page - 1).toString() });
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_PaginationClick, { tab: type });
  };

  const handleTimeChange = (val: React.ChangeEvent<HTMLInputElement>, filterKey: string) => {
    updateQueryParamsAndNavigate({
      [filterKey]: val.target.value
    });
  };
  const handleContactableSwitchChange = () => {
    const isChecked = queryParams?.contactable === 'true';
    updateQueryParamsAndNavigate({ contactable: (!isChecked).toString(), page: 0 });
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_OnlyContactableToggle, {
      tab: type
    });
  };

  const { data: caseDetails } = pageData?.details || {};
  const { startDate } = caseDetails || ({} as CustomerDetailApiResponse);
  const disbursmentDate = startDate + 'T00:00';

  return (
    <div className={styles.systemInteractionsWrapper}>
      <div className={styles.systemInteractionsTopContainer}>
        <GridRow className={cx(styles.w100, 'items-end', styles.commonInteractionsWrapper)}>
          {isTypeFieldVisit && !isPreviousLoanInteraction ? (
            <>
              <div className={cx(styles.dateFilterWrapper, styles.fieldDateFilterWrapper)}>
                <DateTimePickerComponent
                  heading="From"
                  type={DATE_TIME_TYPE.DATE_TIME}
                  id={FROM_TIME}
                  name={FROM_TIME}
                  max={queryParams?.[TO_TIME] || maxInputDate}
                  value={queryParams?.[FROM_TIME] || defaultLastMonthDate}
                  onChange={handleOnFromDateChange}
                />

                <DateTimePickerComponent
                  heading="To"
                  type={DATE_TIME_TYPE.DATE_TIME}
                  id={TO_TIME}
                  name={TO_TIME}
                  max={maxInputDate}
                  min={queryParams?.[FROM_TIME] || defaultLastMonthDate}
                  value={queryParams?.[TO_TIME] || maxInputDate}
                  onChange={handleOnToDateChange}
                  className={styles.datePicker}
                />
              </div>
            </>
          ) : (
            <></>
          )}
          {isTypeFieldVisit ? (
            <GridColumn
              md={isPreviousLoanInteraction ? 12 : 6}
              className={cx('p-0', styles.contactableWrapper)}
            >
              <GridRow
                className={cx(styles.justifyFlexEnd, styles.systemWrapper, styles.commonWrapper)}
              >
                <Switch
                  checked={queryParams?.contactable === 'true'}
                  onChange={handleContactableSwitchChange}
                  headerText="Only contactable"
                />
              </GridRow>
            </GridColumn>
          ) : null}
        </GridRow>
      </div>
      <div className={styles.table}>
        <SuspenseLoader
          loading={!!systemInteractionsTypeLoading}
          fallBack={<TableLoader numberOfLines={6} />}
        >
          <AgTable
            defaultColDef={{
              suppressMovable: true,
              wrapText: true
            }}
            style={{
              height: `100%`
            }}
            gridOptions={{ suppressCellSelection: true }}
            rowData={systemInteractionsTypeData?.data}
            columnDefs={
              isTypeFieldVisit
                ? SYSTEM_INTERACTIONS_FIELD_VISIT_TYPE_COL_DEFS
                : SYSTEM_INTERACTIONS_TYPE_COL_DEFS
            }
            sizeColumnsToFit
            theme={'alpine'}
            noRowsOverlayComponent={() => (
              <Typography variant="p4" className={styles.emptyListText}>
                {NO_RECORD_FOUND}
              </Typography>
            )}
            animateRows
            paginationWrapperClasses={'paginationWrapperClasses'}
            PaginationComponent={
              <Pagination
                totalCount={systemInteractionsTypeData?.pages?.totalElements || 0}
                onPageChange={handlePageChange}
                currentPage={getCurrentPageNumber()}
                onPageSizeChange={handlePageSizeChange}
                pageSize={getCurrentPageSize()}
                pageNumberDropDownPosition={DropDownPosition.TOP}
              />
            }
          />
          {type === SYSTEM_INTERACTIONS_TYPE.FIELD_VISIT && (
            <FeedbackImageLocation interactions={systemInteractionsTypeData} />
          )}
        </SuspenseLoader>
      </div>
    </div>
  );
};

export default SystemInteractions;
